import React, { useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useWidgets, Widget } from "../contexts/WidgetContext";
import { useCustomTheme } from "../contexts/ThemeContext";
import { Button } from "./ui/button";
import { ThemeToggle } from "./ui/theme-toggle";
import { AnimatedGradient } from "./ui/animated-gradient";
import { DynamicFavicon } from "./ui/DynamicFavicon";
import ClockWidget from "./widgets/ClockWidget";
import NotesWidget from "./widgets/NotesWidget";
import RSSFeedWidget from "./widgets/RSSFeedWidget";
import CompanyNewsWidget from "./widgets/CompanyNewsWidget";
import CentralSection from "./sections/CentralSection";
import QuickLinksPill from "./widgets/QuickLinksPill";
import ImageWidget from "./widgets/ImageWidget";
import GalleryWidget from "./widgets/GalleryWidget";
import PomodoroWidget from "./widgets/PomodoroWidget";
import KanbanWidget from "./widgets/KanbanWidget";
import TimeTrackerWidget from "./widgets/TimeTrackerWidget";
import WeatherWidget from "./widgets/WeatherWidget";
import WikiWidget from "./widgets/WikiWidget";
import { StockMarketWidget } from "./widgets/StockMarketWidget";
import { NotificationDemoWidget } from "./widgets/NotificationDemoWidget";
import oneSpaceApi, { offlineEvents } from "../lib/onespace-api";
import storage from "../lib/storage"; // Import storage abstraction
import { SettingsDialog } from "./ui/settings-dialog";
import WidgetWrapper from './WidgetWrapper';
import StatDisplay from "./ui/StatDisplay";
// import { ConnectionStatus } from "./ui/ConnectionStatus"; // Tijdelijk uitgeschakeld
import { getCookie } from "../lib/cookies"; // Cookie utility importeren
import { OnboardingWizard } from "./ui/OnboardingWizard"; // Wizard importeren
import { InitialLoadingSpinner } from "./ui/InitialLoadingSpinner"; // Spinner importeren
import { AnnouncementBanner } from "./ui/AnnouncementBanner"; // Banner importeren
import { PollBanner } from "./ui/PollBanner"; // Nieuwe import
import { PollModal } from "./ui/PollModal"; // Nieuwe import
import { OrganizationSelectorModal } from "./ui/OrganizationSelectorModal"; // Import for organization selector
import { SettingsMenu } from "./ui/settings-menu"; // Import SettingsMenu
import SpotlightManager from "./Spotlight";
import { Bell, Timer, Clock } from "lucide-react";
import { useTimerState, useSettingsRefresh, SettingsData } from "../hooks/useSettingsRefresh";
import LazyWidget from "./LazyWidget";

// Widget types that we can actually render
const AVAILABLE_WIDGETS = ["notes", "rss", "news", "image", "gallery", "pomodoro", "kanban", "timetracker", "stockmarket", "wiki"];

// Grid configuration
const GRID_COLUMNS = 2;

// Local storage keys
const BG_IMAGE_KEY = "onespace_background_image";
const BG_IMAGE_URL_KEY = "onespace_background_image_url";
const SETTINGS_CACHE_KEY = "onespace_settings_cache";
const SETTINGS_TIMESTAMP_KEY = "onespace_settings_timestamp";

// Background object structure from the API
interface BackgroundStyle {
  type: string; // "color", "image", "gradient"
  color: string;
  image?: string | null;
  gradient?: string | null;
}

interface ThemeStyles {
  background: BackgroundStyle;
  text_color: string;
  primary_color: string;
  secondary_color: string;
  favicon?: string;  // Add favicon property
  logo?: string | null;
}

interface WidgetSettings {
  template: string;     // Type widget (notes, rss, etc.)
  title: string;
  width_column: number;
  instance_id?: string; // Optionele unieke ID voor widget instantie
  [key: string]: any;   // Voor andere widget-specifieke instellingen
}

interface WeatherData {
  temperature: number;
  condition: string;
  location: string;
  icon?: string;
}

interface QuickLink {
  title: string;
  url: string;
  icon: string;
}

// Add ApiQuickLink interface to match the backend structure
interface ApiQuickLink {
  id?: string;
  name: string;
  value: string;
  type: 'url' | 'application';
  icon: string | null;
  description?: string | null;
  is_visible?: boolean;
  is_favorite?: boolean;
  team_id?: string | number | null;
  user_id?: string | number | null;
  order?: number;
  created_at?: string;
  updated_at?: string;
}

// Add QuickLinksResponse interface to match CentralSection expectations
interface QuickLinksResponse {
  all: ApiQuickLink[];
  grouped: {
    team_id: string | number | null;
    team_name: string;
    links: ApiQuickLink[];
  }[];
  favorites: ApiQuickLink[];
}

// Interface for a single statistic item
export interface StatItem {
  number: string; 
  prefix?: string | null; 
  suffix?: string | null; 

  change: string; 
  change_prefix?: string | null; // Add optional change_prefix
  change_suffix?: string | null; // Add optional change_suffix

  title: string;
}

interface TimerInfo {
  status: 'running' | 'stopped';
  timestamp: number; // Assuming Unix timestamp
  project: string;
  // widget_instance_id?: string;
}

interface PollOption {
  id: string | number;
  text: string;
}

interface PollResultOption extends PollOption {
  votes: number;
}

interface PollData {
  id: string;
  question: string;
  options: PollOption[];
  allow_custom_option: boolean;
  image_url?: string;
  user_has_voted: boolean;
  results?: PollResultOption[];
  total_votes?: number;
}

// Add countdown target interface
interface CountdownTarget {
  timestamp: number; // Unix timestamp in milliseconds
  label: string;
  description?: string;
}

interface SettingsResponse {
  username?: string;
  background_image?: string;
  styling?: {
    light: ThemeStyles;
    dark: ThemeStyles;
    layout?: 'centered' | 'left-aligned';
  };
  widgets?: WidgetSettings[]; // Now an array instead of an object
  weather?: WeatherData;      // Weather data from the API
  location?: string;          // User's location (city/region)
  quicklinks?: QuickLink[];   // Quick links for the welcome widget
      timer?: TimerInfo; // Add timer info
  stats?: StatItem[]; // Add stats array
  poll?: PollData | null; // Add poll data
  organization_name?: string; // Organization name for the title
  tenant_name?: string; // Legacy field for organization name
  countdown?: CountdownTarget | null; // Add countdown target for dynamic favicon
  central_widgets?: any[]; // Central widgets for dashboard main section
  
  // Possible other fields for organization name
  company_name?: string;
  name?: string;
  org_name?: string;
  tenant?: any; // Tenant object or string
  organization?: any; // Organization object or string
}

// Cookie utility functions for hide widgets
const setCookie = (name: string, value: string, days: number = 30) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${value}; expires=${expires}; path=/`;
};

const Dashboard: React.FC = () => {
  const { isAuthenticated, login, logout, user, isLoadingUser } = useAuth();
  const { widgets, addWidget, clearWidgets } = useWidgets();
  const { currentThemeColors, setThemeStyles, fetchStylingFromAPI } = useCustomTheme();
  const [isDemoMode, setIsDemoMode] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [userSettings, setUserSettings] = useState<SettingsResponse | null>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isReady, setReady] = useState(true);
  const [weatherData, setWeatherData] = useState<WeatherData | null>(null);
  const [isWeatherLoading, setIsWeatherLoading] = useState(true);
  const [activeTimerInfo, setActiveTimerInfo] = useState<TimerInfo | null>(null);
  const [dashboardStats, setDashboardStats] = useState<StatItem[]>([]);
  const [headerStyle, setHeaderStyle] = useState<React.CSSProperties>({});
  const [showWizard, setShowWizard] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  
  // State voor hide widgets functionaliteit
  const [hideWidgets, setHideWidgets] = useState(() => {
    return getCookie('hideWidgets') === 'true';
  });
  
  // State voor poll
  const [currentPoll, setCurrentPoll] = useState<PollData | null>(null);
  const [showPollBanner, setShowPollBanner] = useState(true); // Toon banner initieel
  const [isPollModalOpen, setIsPollModalOpen] = useState(false);
  const [isSubmittingVote, setIsSubmittingVote] = useState(false);
  const [countdownTarget, setCountdownTarget] = useState<CountdownTarget | null>(null);

  // State for organization selector modal
  const [isOrgSelectorOpen, setIsOrgSelectorOpen] = useState(false);

  // State for quick actions
  const [showNotesOverlay, setShowNotesOverlay] = useState(false);
  const [showPomodoroSetup, setShowPomodoroSetup] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showFileTransfer, setShowFileTransfer] = useState(false);

  // Add state for Pomodoro setup form
  const [pomodoroTask, setPomodoroTask] = useState('');
  const [pomodoroDuration, setPomodoroDuration] = useState(25);

  // Add request tracking to prevent duplicate API calls
  const isSettingsFetchPending = useRef(false);

  // Add login loading state
  const [isLoginLoading, setIsLoginLoading] = useState(false);

  // Offline state
  const [isOffline, setIsOffline] = useState(false);
  const [offlineError, setOfflineError] = useState<string | null>(null);
  
  // Timer state using the new hook
  const { timerData, formatTime, isRunning, project } = useTimerState();
  
  // Current time state for footer display
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Listen for settings refresh events
  useSettingsRefresh({
    onSettingsRefresh: (settings: SettingsData) => {
      console.log('🔄 Settings refreshed, refetching user settings...');
      fetchUserSettings(true); // Force refresh
    },
    onTimerRefresh: (timer) => {
      console.log('⏱️ Timer refreshed:', timer);
      // Timer data is automatically handled by useTimerState hook
    }
  });
  
  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);

  // Listen to offline events from API client
  useEffect(() => {
    const unsubscribe = offlineEvents.addListener((event) => {
      setIsOffline(event.isOffline);
      setOfflineError(event.isOffline ? event.errorMessage : null);
      
      if (!event.isOffline) {
        console.log('API connection restored');
      } else {
        console.log('API offline detected:', event.errorMessage);
      }
    });

    return unsubscribe; // Cleanup listener on unmount
  }, []);

  // Effect om focus van omnibox naar pagina te verplaatsen in browser extensie context
  useEffect(() => {
    const isExtensionContext = () => {
      // Detecteer of we in een browser extensie context zitten
      return typeof window !== 'undefined' && 
             window.chrome && 
             window.chrome.runtime && 
             window.chrome.runtime.id;
    };

    const ensurePageFocus = () => {
      // Als we in een browser extensie context zitten, forceer focus naar de pagina
      if (isExtensionContext()) {
        // Eerste poging na 500ms
        setTimeout(() => {
          try {
            // Maak een onzichtbaar focusbaar element
            const focusTarget = document.createElement('div');
            focusTarget.tabIndex = -1;
            focusTarget.style.position = 'fixed';
            focusTarget.style.top = '0';
            focusTarget.style.left = '0';
            focusTarget.style.width = '1px';
            focusTarget.style.height = '1px';
            focusTarget.style.opacity = '0';
            focusTarget.style.pointerEvents = 'none';
            
            document.body.appendChild(focusTarget);
            focusTarget.focus();
            
            // Verwijder het element na een korte delay
            setTimeout(() => {
              if (focusTarget.parentNode) {
                focusTarget.parentNode.removeChild(focusTarget);
              }
            }, 100);
            
            console.log('Focus shifted from omnibox to page for browser extension (first attempt)');
          } catch (error) {
            console.warn('Could not shift focus from omnibox (first attempt):', error);
          }
        }, 500);

        // Tweede poging na 1500ms als fallback
        setTimeout(() => {
          try {
            // Probeer verschillende focus strategieën
            if (document.activeElement && document.activeElement.tagName === 'INPUT') {
              // Als de omnibox nog steeds gefocust is, probeer opnieuw
              const fallbackTarget = document.createElement('button');
              fallbackTarget.style.position = 'absolute';
              fallbackTarget.style.left = '-9999px';
              fallbackTarget.style.width = '1px';
              fallbackTarget.style.height = '1px';
              fallbackTarget.style.opacity = '0';
              
              document.body.appendChild(fallbackTarget);
              fallbackTarget.focus();
              fallbackTarget.blur();
              
              setTimeout(() => {
                if (fallbackTarget.parentNode) {
                  fallbackTarget.parentNode.removeChild(fallbackTarget);
                }
              }, 100);
              
              console.log('Focus shifted from omnibox to page for browser extension (fallback attempt)');
            }
          } catch (error) {
            console.warn('Could not shift focus from omnibox (fallback attempt):', error);
          }
        }, 1500);
      }
    };

    // Voer uit bij mount
    ensurePageFocus();

    // Voer ook uit wanneer de pagina focus krijgt (voor het geval de gebruiker terug navigeert)
    const handleWindowFocus = () => {
      ensurePageFocus();
    };

    window.addEventListener('focus', handleWindowFocus);

    return () => {
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, []);

  // Check cookie en auth status bij mount
  useEffect(() => {
    const demoCookie = getCookie('demo');
    // Toon wizard als gebruiker niet is ingelogd EN de demo cookie niet op 1 staat
    if (!isAuthenticated && !isLoadingUser && demoCookie !== '1') {
      setShowWizard(true);
    } else {
      setShowWizard(false);
    }
  }, [isAuthenticated, isLoadingUser]); // isLoadingUser als dependency toevoegen

  // When component mounts, immediately load cached data and then fetch fresh data
  useEffect(() => {
    // Immediately try to load from cache
    const loadInitialData = async () => {
      // First try to load from cache immediately
      const loadedFromCache = await loadSettingsFromCache();
      
      // If we're authenticated, then start fetching fresh data in the background
      if (isAuthenticated) {
        fetchUserSettings(false, loadedFromCache); // Pass loadedFromCache flag to avoid spinner if we already have data
      }
      
      // If we loaded cached data, we can hide the initial loading spinner
      if (loadedFromCache) {
        setIsInitialLoading(false);
      }
    };
    
    loadInitialData();
    
    // Also fetch styling immediately from the API
    fetchStylingFromAPI(false, true);
  }, [isAuthenticated]); // Re-run when auth state changes

  // Enhanced fetchUserSettings to support the cache-first strategy
  const fetchUserSettings = async (skipCache = false, alreadyLoadedFromCache = false) => {
    // If not skipping cache and not already loaded from cache, try to load from cache
    if (!skipCache && !alreadyLoadedFromCache) {
      const loadedFromCache = await loadSettingsFromCache();
      if (loadedFromCache) {
        console.log('Loaded settings from cache, refreshing in background');
        
        // If the user is not authenticated, stop here after loading from cache
        if (!isAuthenticated) return;
      }
    }
    
    // Only call API if user is authenticated
    if (!isAuthenticated) {
      // If not authenticated and no cache, still hide spinner
        if (isLoading) setIsLoading(false);
        return;
    }
    
    // Prevent duplicate API calls
    if (isSettingsFetchPending.current && !skipCache) {
      console.log('Settings fetch already in progress, skipping duplicate request');
      return;
    }
    
    isSettingsFetchPending.current = true; // Set flag before API call
    
    // Only show loading spinner if we don't have any data yet
    // (either from cache or previous API call)
    if ((skipCache || !userSettings) && !alreadyLoadedFromCache) {
      setIsLoading(true);
      setIsWeatherLoading(true);
      setDashboardStats([]);
    }
    
    try {
      // Add cache-busting parameter if needed
      const url = skipCache 
        ? `/api/settings/general?_=${Date.now()}` 
        : '/api/settings/general';
        
      const response = await oneSpaceApi.get<SettingsResponse>(url);
      
      // Process the response data...
      // ... existing code for processing response data ...
      
      // Log the entire response object to inspect all available fields
      console.log('Settings Response RAW:', response.data);
      
      if (response.data) {
        // Cache the response in localStorage for future use
        cacheSettings(response.data);
        
        // Existing code for processing the response...
        setUserSettings(response.data);
        
        // Process styling options if they exist
        if (response.data.styling) {
          // Make sure favicon URLs are carried over from the API response
          if (response.data.styling.dark && response.data.styling.dark.favicon) {
            console.log('>>> Dark theme favicon detected:', response.data.styling.dark.favicon);
          }
          if (response.data.styling.light && response.data.styling.light.favicon) {
            console.log('>>> Light theme favicon detected:', response.data.styling.light.favicon);
          }
          
          setThemeStyles(response.data.styling);
        } else if (response.data.background_image) {
          // Create a styling object with the legacy background image
          const legacyStyles = {
            light: {
              background: {
                type: "image",
                color: "#ffffff",
                image: response.data.background_image,
                gradient: null
              },
              text_color: '#000000',
              primary_color: '#000000',
              secondary_color: '#ff9900'
            },
            dark: {
              background: {
                type: "image",
                color: "#000000",
                image: response.data.background_image,
                gradient: null
              },
              text_color: '#ffffff',
              primary_color: '#ffffff',
              secondary_color: '#ff9900'
            }
          };
          
          // Apply legacy styles to theme
          setThemeStyles(legacyStyles);
          
          // Also cache the generated background image for better performance
          handleLegacyBackgroundImage(response.data.background_image);
        }
        
        // Set weather data if available
        if (response.data.weather) {
          setWeatherData(response.data.weather);
        } else if (response.data.location) {
          // If only location is provided, fetch weather separately
          fetchWeatherData(response.data.location);
        }

        // Process Timer Info
        if (response.data.timer && response.data.timer.status === 'running') {
          setActiveTimerInfo(response.data.timer);
          console.log('>>> Active timer DETECTED, setting state:', response.data.timer);
        } else {
          setActiveTimerInfo(null); // Ensure it's null if timer is stopped or not present
          console.log('>>> Active timer NOT detected or status !== running.');
        }

        // Process Stats
        if (response.data.stats && Array.isArray(response.data.stats)) {
          setDashboardStats(response.data.stats);
          console.log('>>> Dashboard stats DETECTED:', response.data.stats);
        } else {
          setDashboardStats([]);
          console.log('>>> Dashboard stats NOT detected or not an array.');
        }

        // Process Poll Data
        if (response.data.poll) {
          const pollData = response.data.poll;
          // Controleer of gebruiker al gestemd heeft (op basis van localstorage of API)
          const hasVoted = await checkUserHasVoted(pollData.id);
          setCurrentPoll({ ...pollData, user_has_voted: hasVoted });
          
          // Reset banner zichtbaarheid als er een nieuwe poll is
          const dismissed = sessionStorage.getItem(`pollBannerDismissed_${pollData.id}`);
          setShowPollBanner(!dismissed);
        } else {
          setCurrentPoll(null);
          setShowPollBanner(false);
        }

        // Process Countdown Data
        if (response.data.countdown) {
          setCountdownTarget(response.data.countdown);
          console.log('>>> Countdown DETECTED:', response.data.countdown);
        } else {
          setCountdownTarget(null);
          console.log('>>> Countdown NOT detected in API response.');
        }
      }
    } catch (err) {
      console.error("Error fetching user settings:", err);
      // Error handling is now centralized in the API client
      // The offline state will be updated via the event listener
    } finally {
      setIsLoading(false);
      setIsWeatherLoading(false);
      isSettingsFetchPending.current = false; // Reset flag after API call
    }
  };

  // Functie om settings in cache op te slaan
  const cacheSettings = (settings: SettingsResponse) => {
    try {
      storage.setItem(SETTINGS_CACHE_KEY, JSON.stringify(settings));
      storage.setItem(SETTINGS_TIMESTAMP_KEY, Date.now().toString());
      console.log('Cached settings in localStorage');
    } catch (error) {
      console.error('Error caching settings in localStorage:', error);
    }
  };
  
  // Enhanced function to load settings from cache
  const loadSettingsFromCache = async (): Promise<boolean> => {
    try {
      const cachedSettings = await storage.getItem(SETTINGS_CACHE_KEY);
      
      if (!cachedSettings) {
        return false; // Nothing in cache
      }
      
        const parsedSettings = JSON.parse(cachedSettings) as SettingsResponse;
        console.log('Loading settings from cache:', parsedSettings);
        
      // Apply cached settings to state immediately
        setUserSettings(parsedSettings);
        
      // Process styling options if they exist
        if (parsedSettings.styling) {
          // Log favicon URLs from cache
          if (parsedSettings.styling.dark && parsedSettings.styling.dark.favicon) {
            console.log('>>> Dark theme favicon from cache:', parsedSettings.styling.dark.favicon);
          }
          if (parsedSettings.styling.light && parsedSettings.styling.light.favicon) {
            console.log('>>> Light theme favicon from cache:', parsedSettings.styling.light.favicon);
          }
          
          setThemeStyles(parsedSettings.styling);
        } else if (parsedSettings.background_image) {
          // Create a styling object with the legacy background image
          const legacyStyles = {
            light: {
              background: {
                type: "image",
                color: "#ffffff",
                image: parsedSettings.background_image,
                gradient: null
              },
              text_color: '#000000',
              primary_color: '#000000',
              secondary_color: '#ff9900'
            },
            dark: {
              background: {
                type: "image",
                color: "#000000",
                image: parsedSettings.background_image,
                gradient: null
              },
              text_color: '#ffffff',
              primary_color: '#ffffff',
              secondary_color: '#ff9900'
            }
          };
          
          // Apply legacy styles to theme
          setThemeStyles(legacyStyles);
        }
        
        // Set weather data if available
        if (parsedSettings.weather) {
          setWeatherData(parsedSettings.weather);
          setIsWeatherLoading(false);
        } else if (parsedSettings.location) {
          // If only location is provided, fetch weather separately
          fetchWeatherData(parsedSettings.location);
        }
        
        // Process Timer Info
        if (parsedSettings.timer && parsedSettings.timer.status === 'running') {
          setActiveTimerInfo(parsedSettings.timer);
        } else {
          setActiveTimerInfo(null);
        }
        
        // Process Stats
        if (parsedSettings.stats && Array.isArray(parsedSettings.stats)) {
          setDashboardStats(parsedSettings.stats);
        } else {
          setDashboardStats([]);
        }
        
        // Process Countdown Data from cache
        if (parsedSettings.countdown) {
          setCountdownTarget(parsedSettings.countdown);
        } else {
          setCountdownTarget(null);
        }
        
      // Indicate that we've loaded data from cache successfully
      return true;
    } catch (error) {
      console.error('Error loading settings from cache:', error);
      return false;
    }
  };

  // Legacy support voor oude background image functionaliteit
  const handleLegacyBackgroundImage = async (imageUrl: string) => {
    const storedImageUrl = localStorage.getItem(BG_IMAGE_URL_KEY);
    
    // Only download if the URL is different from the stored one
    if (imageUrl !== storedImageUrl) {
      try {
        // Create a new image to load it
        const img = new Image();
        img.crossOrigin = "anonymous";
        
        img.onload = () => {
          // Create a canvas to resize and compress the image
          const canvas = document.createElement('canvas');
          
          // Resize image to max dimensions of 1200px width
          const MAX_WIDTH = 1200;
          let width = img.width;
          let height = img.height;
          
          if (width > MAX_WIDTH) {
            height = Math.floor(height * (MAX_WIDTH / width));
            width = MAX_WIDTH;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Draw the image on the canvas with the new dimensions
          const ctx = canvas.getContext('2d');
          if (!ctx) return;
          
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to base64 with compression (0.7 quality)
          try {
            const base64data = canvas.toDataURL('image/jpeg', 0.7);
            
            try {
              // Store URL for comparison in future
              localStorage.setItem(BG_IMAGE_URL_KEY, imageUrl);
              
              // Try to store the compressed image
              localStorage.setItem(BG_IMAGE_KEY, base64data);
            } catch (storageError) {
              console.warn("Could not save image to localStorage:", storageError);
            }
          } catch (compressionError) {
            console.error("Error compressing image:", compressionError);
          }
        };
        
        img.onerror = () => {
          console.error("Error loading background image");
        };
        
        // Start loading the image
        img.src = imageUrl;
      } catch (error) {
        console.error("Error processing background image:", error);
      }
    }
  };

  // Fetch weather data based on location
  const fetchWeatherData = async (location: string) => {
    setIsWeatherLoading(true);
    try {
      // This would be a separate API endpoint for weather specifically
      const response = await oneSpaceApi.get(`/api/weather?location=${encodeURIComponent(location)}`);
      if (response.data) {
        setWeatherData(response.data);
      }
    } catch (err) {
      console.error("Error fetching weather data:", err);
    } finally {
      setIsWeatherLoading(false);
    }
  };

  // Handle widget settings updates from WidgetWrapper
  const handleWidgetSettingsUpdate = async (widgetId: string, updatedSettings: any) => {
    console.log(`Updating settings for widget ${widgetId}:`, updatedSettings);
    setIsLoading(true); // Indicate loading state
    try {
      // TODO: Implement API call to save widget settings
      // Example: await oneSpaceApi.put(`/api/widgets/${widgetId}/settings`, { settings: updatedSettings });
      
      // After successful save, refetch all settings to update the dashboard
      await fetchUserSettings(true); // Use cache-busting
    } catch (error) {
      console.error("Error updating widget settings:", error);
      // Optionally show an error message to the user
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize widgets once when component mounts or when settings are loaded
  useEffect(() => {
    const initializeWidgets = () => {
      const demoCookie = getCookie('demo');
      const useDemoWidgets = demoCookie === '1';
      
      // Clear existing widgets
      clearWidgets();
      
      let widgetConfigs: Array<{ 
        type: string; 
        settings: WidgetSettings;
        order: number;
      }> = [];
      
      if (isAuthenticated && userSettings?.widgets) {
        // Gebruik API settings als gebruiker is ingelogd
        widgetConfigs = userSettings.widgets
          .filter(widget => AVAILABLE_WIDGETS.includes(widget.template))
          .map((widget, index) => ({
            type: widget.template,
            settings: widget,
            order: index
          }));
        setIsDemoMode(false);
      } else if (useDemoWidgets) {
        // Gebruik demo widgets als de cookie is gezet en gebruiker niet is ingelogd
        setIsDemoMode(true);
        widgetConfigs = [
          {
            type: "notes",
            settings: { 
              template: "notes",
              title: "Notities", 
              width_column: 2 
            },
            order: 0
          },
          // {
          //   type: "rss",
          //   settings: { 
          //     template: "rss",
          //     title: "RSS", 
          //     width_column: 1,
          //     feeds: [
          //       "https://tweakers.net/feeds/nieuws.xml",
          //       "https://www.nu.nl/rss"
          //     ],
          //     limit: 3,
          //     sort: "desc",
          //     sort_by: "published_at"
          //   },
          //   order: 1
          // },
          {
            type: "news",
            settings: { 
              template: "news",
              title: "Bedrijfsnieuws", 
              width_column: 1 
            },
            order: 2
          },
          // {
          //   type: "image",
          //   settings: { 
          //     template: "image",
          //     title: "Voorbeeld afbeelding", 
          //     width_column: 1,
          //     url: "https://momentum.photos/img/5de588ce-6afd-4a6e-9a43-28e7646c3e60.jpg?momo_cache_bg_uuid=4ece22b4-a6f1-4866-beae-2a17204fd66d"
          //   },
          //   order: 3
          // },
          // {
          //   type: "gallery",
          //   settings: { 
          //     template: "gallery",
          //     title: "Voorbeeld afbeeldingen", 
          //     width_column: 1,
          //     show_image_time: 5000, // 5 seconds per image
          //     images: [
          //       "https://momentum.photos/img/5de588ce-6afd-4a6e-9a43-28e7646c3e60.jpg?momo_cache_bg_uuid=4ece22b4-a6f1-4866-beae-2a17204fd66d",
          //       "https://momentum.photos/img/5de588ce-6afd-4a6e-9a43-28e7646c3e60.jpg?momo_cache_bg_uuid=4ece22b4-a6f1-4866-beae-2a17204fd66d",
          //       "https://momentum.photos/img/5de588ce-6afd-4a6e-9a43-28e7646c3e60.jpg?momo_cache_bg_uuid=4ece22b4-a6f1-4866-beae-2a17204fd66d",
          //       "https://momentum.photos/img/5de588ce-6afd-4a6e-9a43-28e7646c3e60.jpg?momo_cache_bg_uuid=4ece22b4-a6f1-4866-beae-2a17204fd66d"
          //     ]
          //   },
          //   order: 4
          // },
          {
            type: "pomodoro",
            settings: { 
              template: "pomodoro",
              title: "Pomodoro", 
              width_column: 1 
            },
            order: 5
          },
          {
            type: "kanban",
            settings: { 
              template: "kanban",
              title: "Kanban", 
              width_column: 1 
            },
            order: 6
          },
          {
            type: "timetracker",
            settings: { 
              template: "timetracker",
              title: "Time Tracker", 
              width_column: 1 
            },
            order: 7
          },
          {
            type: "wiki",
            settings: { 
              template: "wiki",
              title: "Team Wiki", 
              width_column: 2,
              team_id: null,
              max_items: 5,
              show_create_button: true,
              compact_mode: false
            },
            order: 8
          }
        ];
      } else {
        // Geen widgets tonen als geen demo cookie en niet ingelogd
        setIsDemoMode(false);
        // widgetConfigs blijft leeg
      }
      
      // Sort by order (although it's probably already in the right order from the API)
      widgetConfigs.sort((a, b) => a.order - b.order);
      
      // Bijhouden van huidige positie in het grid
      let currentRow = 0;    // Huidige rij
      let currentColumn = 0; // Huidige kolom (0 voor links, 1 voor rechts)
      
      // Widgets toevoegen in de opgegeven volgorde
      widgetConfigs.forEach(({ type, settings }) => {
        // Widget neemt 1 of 2 kolommen in
        const columnSpan = settings.width_column === 2 ? 2 : 1;
        
        // Check of we naar de volgende rij moeten gaan
        if (currentColumn + columnSpan > GRID_COLUMNS) {
          currentRow++;
          currentColumn = 0;
        }
        
        // Genereer een unieke instance ID als deze niet is opgegeven
        const instanceId = settings.instance_id || `${type}_${Math.random().toString(36).substr(2, 9)}`;
        
        // Widget toevoegen op huidige positie
        addWidget({
          type,
          title: settings.title || type,
          position: { x: currentColumn, y: currentRow },
          size: { width: columnSpan, height: 1 }, // Hoogte is altijd 1 rij
          instanceId,
          // Voeg alle andere settings toe, behalve degenen die we al expliciet hebben gebruikt
          ...Object.entries(settings)
            .filter(([key]) => !['template', 'title', 'width_column', 'instance_id'].includes(key))
            .reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {})
        });
        
        // Update huidige kolom
        currentColumn += columnSpan;
        
        // Als we aan het einde van een rij zijn, ga naar de volgende rij
        if (currentColumn >= GRID_COLUMNS) {
          currentRow++;
          currentColumn = 0;
        }
      });
    };

    // Initialize alleen als we de wizard niet tonen
    if (!showWizard && (isAuthenticated || getCookie('demo') === '1')) {
      initializeWidgets();
    } else if (!showWizard) {
      // Als wizard niet getoond wordt en geen demo cookie, clear widgets
      clearWidgets();
    }
    // userSettings is nu een dependency zodat widgets opnieuw initialiseren als ze veranderen
  }, [isAuthenticated, userSettings, showWizard, addWidget, clearWidgets]);

  // Use a demo weather for non-authenticated users
  useEffect(() => {
    if (!isAuthenticated && !weatherData) {
      // Set a default demo weather
      setWeatherData({
        temperature: 14,
        condition: 'partly cloudy',
        location: 'Haarlem'
      });
      setIsWeatherLoading(false);
    }
  }, [isAuthenticated, weatherData]);

  // Effect hook voor het scroll-effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const maxScroll = 150; // Max scroll afstand voor volledig effect (pas aan naar wens)
      const scrollFraction = Math.min(scrollY / maxScroll, 1); // Fractie (0 tot 1)

      // Bepaal schaal (van 1 naar bijv. 0.9)
      const minScale = 0.9;
      const scale = 1 - scrollFraction * (1 - minScale);

      // Bepaal verticale verschuiving (van 0 naar bijv. -20px)
      const maxTranslateY = -20;
      const translateY = scrollFraction * maxTranslateY;

      setHeaderStyle({
        transform: `scale(${scale}) translateY(${translateY}px)`,
        opacity: 1 - scrollFraction * 0.3 // Optioneel: lichte fade-out
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // Roep direct aan bij laden voor initiële positie (indien pagina al gescrolld is)
    handleScroll(); 

    // Cleanup listener
    return () => window.removeEventListener('scroll', handleScroll);
  }, []); // Lege dependency array: run alleen on mount/unmount

  const renderWidget = (widget: Widget, index: number) => {
    // Determine if this widget should be lazy loaded
    // Only load widgets that are not in the first row (index > 1 for 2-column grid)
    const shouldLazyLoad = index >= 2; // First 2 widgets load immediately, rest are lazy loaded
    
    // Estimate widget height based on type for better placeholder
    const getWidgetHeight = (type: string) => {
      switch (type) {
        case "notes":
          return 400;
        case "rss":
        case "news":
          return 350;
        case "image":
        case "gallery":
          return 300;
        case "pomodoro":
        case "timetracker":
          return 250;
        case "kanban":
          return 450;
        case "stockmarket":
        case "wiki":
          return 320;
        default:
          return 300;
      }
    };

    const renderWidgetComponent = () => {
      switch (widget.type) {
        case "notes":
          return <NotesWidget {...widget} />;
        case "rss":
          return <RSSFeedWidget {...widget} isDemo={isDemoMode} />;
        case "news":
          return <CompanyNewsWidget {...widget} isDemo={isDemoMode} />;
        case "image":
          return <ImageWidget {...widget} />;
        case "gallery":
          return <GalleryWidget {...widget} />;
        case "pomodoro":
          return <PomodoroWidget {...widget} />;
        case "kanban":
          return <KanbanWidget {...widget} />;
        case "timetracker":
          return <TimeTrackerWidget {...widget} />;
        case "stockmarket":
          return <StockMarketWidget {...widget} />;
        case "wiki":
          return <WikiWidget {...widget} />;
        case "notificationDemo":
          return <NotificationDemoWidget {...widget} />;
        default:
          return null;
      }
    };

    const widgetContent = (
      <WidgetWrapper
        title={widget.title}
        widgetId={widget.instanceId || widget.id}
        widgetType={widget.type}
        settings={widget}
        isPinned={widget.isPinned || false}
        isTeamWidget={widget.isTeamWidget || false}
        permission={isAuthenticated ? 'edit' : 'view'}
        onSettingsUpdate={(updatedSettings) => handleWidgetSettingsUpdate(widget.instanceId || widget.id, updatedSettings)}
      >
        {renderWidgetComponent()}
      </WidgetWrapper>
    );

    // If should lazy load, wrap with LazyWidget
    if (shouldLazyLoad) {
      return (
        <LazyWidget
          key={widget.instanceId || widget.id}
          height={getWidgetHeight(widget.type)}
          threshold={0.1}
          rootMargin="100px"
        >
          {widgetContent}
        </LazyWidget>
      );
    }

    // For immediate load widgets, return content directly
    return widgetContent;
  };

  const handleLogin = () => {
    setIsLoginLoading(true);
    // De URL van je backend login endpoint, uit environment variabele
    const apiUrl = import.meta.env.VITE_API_URL || 'https://onespace-app.test';
    const loginUrl = `${apiUrl}/initiate-login`;
    
    // Simplify the login flow - use index.html as the callback directly
    // This works better for Chrome extensions
    const callbackUrl = `${window.location.origin}/index.html`;
    
    // Redirect naar de login pagina met de callback URL als parameter
    window.location.href = `${loginUrl}?callback_url=${encodeURIComponent(callbackUrl)}`;
  };

  // Reload widgets after settings have been saved
  const handleSettingsSave = async () => {
    setReady(false);
    
    // Forceer een refresh van de styling van API
    console.log("Forcefully refreshing styling from API");
    await fetchStylingFromAPI(true);
    
    // Haal ook de gebruikersdata opnieuw op met cache-busting
    await fetchUserSettings(true);
    
    setReady(true);
  };

  // State voor notificaties
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);

  // Handler voor het ophalen van notificaties
  const fetchNotifications = async () => {
    try {
      // Alleen ophalen als gebruiker is ingelogd
      if (!isAuthenticated) return;

      const response = await oneSpaceApi.get('/api/notifications');
      
      if (response.data) {
        // Update notifications state
        setNotifications(response.data);
        
        // Update unread count
        const unreadCount = response.data.filter((n: any) => !n.read_at).length;
        setUnreadNotifications(unreadCount);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
      // Error handling is now centralized in the API client
      // The offline state will be updated via the event listener
    }
  };

  // Effect om notificaties periodiek op te halen
  useEffect(() => {
    // Direct ophalen bij mount
    fetchNotifications();

    // Setup interval voor periodieke updates (elke 30 seconden)
    const interval = setInterval(fetchNotifications, 30000);

    // Cleanup interval bij unmount
    return () => clearInterval(interval);
  }, [isAuthenticated]); // Herhaal effect als auth status verandert

  // Effect voor keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Verbeterde detectie van input velden en gefocuste elementen
      const target = event.target as HTMLElement;
      const isTyping = target.tagName === 'INPUT' || 
                       target.tagName === 'TEXTAREA' || 
                       target.contentEditable === 'true' ||
                       target.isContentEditable ||
                       (target.getAttribute && target.getAttribute('contenteditable') === 'true');

      // Detecteer of we in een browser extensie context zitten
      const isExtensionContext = typeof window !== 'undefined' && 
                                 window.chrome && 
                                 window.chrome.runtime && 
                                 window.chrome.runtime.id;

      if (event.key === 'Escape') {
        // Sluit alle overlays in Dashboard
        setShowNotesOverlay(false);
        setShowPomodoroSetup(false);
        setShowNotifications(false);
        setShowFileTransfer(false);
        setIsSettingsOpen(false);
        setIsPollModalOpen(false);
        setIsOrgSelectorOpen(false);
        
        console.log('Escape key pressed - closing Dashboard overlays');
      } else if (event.key === 'h' && (event.metaKey || event.ctrlKey)) {
        // CMD+H (Mac) of Ctrl+H (Windows) om widgets te verbergen/tonen
        
        // Voor browser extensies: altijd toestaan, zelfs als omnibox gefocust is
        // Voor normale webpagina's: alleen als we niet aan het typen zijn
        const shouldAllowShortcut = isExtensionContext || !isTyping;
        
        if (shouldAllowShortcut) {
          event.preventDefault(); // Voorkom browser defaults
          event.stopPropagation(); // Stop event propagatie
          
          const newHideWidgets = !hideWidgets;
          setHideWidgets(newHideWidgets);
          setCookie('hideWidgets', newHideWidgets.toString(), 30);
          
          console.log(`${event.metaKey ? 'CMD' : 'Ctrl'}+H pressed - ${newHideWidgets ? 'hiding' : 'showing'} widgets${isExtensionContext ? ' (browser extension context)' : ''}`);
        }
      }
    };

    // Voeg event listener toe op document level (zonder capture phase)
    document.addEventListener('keydown', handleKeyDown);
    
    // Voor browser extensies: voeg ook een listener toe op window level voor extra betrouwbaarheid
    const isExtensionContext = typeof window !== 'undefined' && 
                               window.chrome && 
                               window.chrome.runtime && 
                               window.chrome.runtime.id;
    if (isExtensionContext) {
      window.addEventListener('keydown', handleKeyDown, true); // Met capture phase
    }

    // Cleanup event listener bij unmount
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (isExtensionContext) {
        window.removeEventListener('keydown', handleKeyDown, true);
      }
    };
  }, [hideWidgets]); // hideWidgets als dependency om de huidige state te kunnen lezen

  // Handler voor het voltooien van de wizard (nieuwe organisatie)
  const handleCompleteWizard = (companyName: string, userName: string) => {
    console.log('Wizard voltooid voor nieuwe organisatie:', { companyName, userName });
    // TODO: Implementeer API call om nieuwe organisatie/gebruiker te registreren
    setShowWizard(false); 
  };

  // Functie om te controleren of gebruiker gestemd heeft (gebruikt localStorage)
  const checkUserHasVoted = async (pollId: string): Promise<boolean> => {
    // In een echte applicatie zou je dit misschien via de API checken
    const voted = await storage.getItem(`voted_poll_${pollId}`);
    return !!voted;
  };

  // Functie om stem te verwerken
  const handleSubmitVote = async (pollId: string, optionId: string | number | null, customValue?: string) => {
    if (!currentPoll) return;
    setIsSubmittingVote(true);
    try {
      // TODO: Implementeer API call om stem op te slaan
      // await oneSpaceApi.post(`/api/polls/${pollId}/vote`, { option_id: optionId, custom_value: customValue });
      console.log('Submitting vote:', { pollId, optionId, customValue });
      
      // Markeer als gestemd in localStorage
      await storage.setItem(`voted_poll_${pollId}`, 'true');
      
      // Update poll state om resultaten te tonen (simulatie)
      // In een echte app haal je de resultaten op via een nieuwe API call
      setCurrentPoll(prev => prev ? {
        ...prev,
        user_has_voted: true,
        // Simulatie van resultaten - vervang door echte API call
        results: prev.options.map(opt => ({ ...opt, votes: Math.floor(Math.random() * 50) })),
        total_votes: prev.options.length * 25 // Dummy totaal
      } : null);

    } catch (error) {
      console.error("Error submitting vote:", error);
      alert("Kon stem niet verwerken.");
    } finally {
      setIsSubmittingVote(false);
    }
  };
  
  // Update page title when settings are loaded
  useEffect(() => {
    if (userSettings) {
      // Check alle mogelijke velden voor organisatienaam
      let orgName = null;
      
      // Direct velden
      if (userSettings.organization_name) orgName = userSettings.organization_name;
      else if (userSettings.tenant_name) orgName = userSettings.tenant_name;
      else if (userSettings.company_name) orgName = userSettings.company_name;
      else if (userSettings.name) orgName = userSettings.name;
      else if (userSettings.org_name) orgName = userSettings.org_name;
      
      // Embedded objects
      else if (userSettings.organization && typeof userSettings.organization === 'object' && userSettings.organization.name) {
        orgName = userSettings.organization.name;
      }
      else if (userSettings.tenant && typeof userSettings.tenant === 'object' && userSettings.tenant.name) {
        orgName = userSettings.tenant.name;
      }
      
      // Set de document titel
      if (orgName) {
        document.title = `${orgName}`;
        console.log('Set document title to:', document.title);
      } else {
        document.title = 'Onespace';
      }
    } else if (!isLoadingUser && !isAuthenticated) {
      // Reset title when logged out
      document.title = 'Onespace';
    }
  }, [userSettings, isAuthenticated, isLoadingUser]);

  // Helper function to extract and set the organization name from the response
  const setOrganizationNameFromResponse = (data: SettingsResponse) => {
    // Store the current data for comparison
    const originalData = { ...data };
    
    // Check for organization name in common API response structures
    if (!data.organization_name) {
      // Try common patterns
      if (data.tenant && typeof data.tenant === 'object' && data.tenant.name) {
        data.organization_name = data.tenant.name;
      } else if (data.organization && typeof data.organization === 'object' && data.organization.name) {
        data.organization_name = data.organization.name;
      } else if (data.tenant && typeof data.tenant === 'string') {
        data.organization_name = data.tenant;
      } else if (data.organization && typeof data.organization === 'string') {
        data.organization_name = data.organization;
      } else if (data.company_name) {
        data.organization_name = data.company_name;
      } else if (data.tenant_name) {
        data.organization_name = data.tenant_name;
      }
      
      // Log if we found and set a new organization name
      if (data.organization_name !== originalData.organization_name) {
        console.log('Found and set organization_name:', data.organization_name);
      }
    }
  };

  const getFaviconUrl = () => {
    if (!userSettings?.styling) return undefined;
    
    // Use dark mode favicon if in dark mode, otherwise use light mode favicon
    const isDarkMode = document.documentElement.classList.contains('dark');
    const themeKey = isDarkMode ? 'dark' : 'light';
    
    if (userSettings.styling[themeKey]?.favicon) {
      return userSettings.styling[themeKey].favicon;
    }
    
    // Fallback to the other theme's favicon if the current theme doesn't have one
    const otherThemeKey = isDarkMode ? 'light' : 'dark';
    if (userSettings.styling[otherThemeKey]?.favicon) {
      return userSettings.styling[otherThemeKey].favicon;
    }
    
    return undefined;
  };

  // Helper function to format quicklinks into the expected QuickLinksResponse format
  const formatQuickLinks = (quicklinks: any): QuickLinksResponse => {
    // If quicklinks is already in correct format with all/grouped properties
    if (quicklinks && 'all' in quicklinks && 'grouped' in quicklinks) {
      // Ensure favorites is included
      const formatted = quicklinks as QuickLinksResponse;
      if (!formatted.favorites) {
        formatted.favorites = [];
      }
      return formatted;
    }
    
    // If quicklinks is an array of QuickLink objects
    if (Array.isArray(quicklinks)) {
      // Transform QuickLink[] to ApiQuickLink[]
      const apiQuickLinks: ApiQuickLink[] = quicklinks.map(link => ({
        id: String(Math.random()), // Generate a random ID
        name: link.title,
        value: link.url,
        type: 'url', // Default to url type
        icon: link.icon,
        is_visible: true
      }));

      return {
        all: apiQuickLinks,
        grouped: [{
          team_id: null,
          team_name: 'Personal',
          links: apiQuickLinks
        }],
        favorites: []
      };
    }
    
    // Default empty structure
    return { all: [], grouped: [], favorites: [] };
  };

  // Handler voor het starten van Pomodoro timer
  const handleStartPomodoro = async () => {
    try {
      // Alleen als gebruiker is ingelogd
      if (!isAuthenticated) return;

      const currentTimestamp = Math.floor(Date.now() / 1000); // Unix timestamp in seconds
      
      // API call naar /settings/update/pomodoro
      const response = await oneSpaceApi.post('/api/settings/update/pomodoro', {
        timestamp: currentTimestamp,
        project: pomodoroTask || 'Untitled Task',
        duration: pomodoroDuration
      });

      if (response.data) {
        // Update active timer info in state
        setActiveTimerInfo({
          status: 'running',
          timestamp: currentTimestamp,
          project: pomodoroTask || 'Untitled Task'
        });

        // Reset form
        setPomodoroTask('');
        setPomodoroDuration(25);
        
        // Close overlay
        setShowPomodoroSetup(false);

        console.log('Pomodoro timer started successfully');
      }
    } catch (error) {
      console.error("Error starting Pomodoro timer:", error);
      // Error handling is now centralized in the API client
      // The offline state will be updated via the event listener
    }
  };

  // Monitor online/offline status (browser level)
  useEffect(() => {
    const handleOnline = () => {
      // Only clear offline if it was caused by browser being offline
      if (!navigator.onLine) return;
      
      // Retry fetching data when coming back online
      if (isAuthenticated) {
        fetchUserSettings(true); // Force refresh
      }
    };

    const handleOffline = () => {
      setIsOffline(true);
      setOfflineError('Geen internetverbinding beschikbaar');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial check
    if (!navigator.onLine) {
      handleOffline();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [isAuthenticated]);

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* 1. Auth laden: Toon niets (of een minimale placeholder) */}
      {isLoadingUser && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">{/* Optionele minimale loader */}</div>
      )}
      
      {/* 2. Offline Check - EERST controleren, ongeacht auth status */}
      {!isLoadingUser && isOffline && (
        <>
          {/* Achtergrond op basis van thema instellingen */}
          {currentThemeColors.background.image ? (
            <div className="fixed inset-0 overflow-hidden" style={{ 
              backgroundImage: `url(${currentThemeColors.background.image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundAttachment: 'fixed',
              width: '100%',
              height: '100%',
              zIndex: -1,
            }}>
              <div className="absolute inset-0" style={{ 
                backgroundColor: `${currentThemeColors.background.color}`, 
                opacity: 0.25,
                backdropFilter: 'blur(1px)'
              }} />
            </div>
          ) : (
            <div className="fixed inset-0 z-[-1]" style={{ 
              backgroundColor: currentThemeColors.background.color 
            }}>
              <AnimatedGradient />
            </div>
          )}

          {/* Offline Placeholder Content */}
          <div className="relative z-10 min-h-screen w-full flex items-center justify-center text-theme">
            <div className="text-center px-6 py-12 max-w-md mx-auto">
              {/* Broken Connection SVG Icon */}
              <svg 
                className="w-24 h-24 mx-auto mb-6 text-theme opacity-60" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth="1.5" 
                  d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0"
                />
                {/* X mark to indicate broken connection */}
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth="2" 
                  d="M18 6L6 18M6 6l12 12"
                  className="text-red-500"
                />
              </svg>
              
              {/* Main Message */}
              <h1 className="text-2xl md:text-3xl font-bold text-theme mb-4">
                Het lijkt alsof u offline bent
              </h1>
              
              {/* Error Details */}
              {offlineError && (
                <p className="text-theme-70 mb-6 text-lg">
                  {offlineError}
                </p>
              )}
              
              {/* Instructions */}
              <p className="text-theme-60 mb-8">
                Controleer uw internetverbinding en probeer het opnieuw.
              </p>
              
              {/* Retry Button */}
              <button
                onClick={() => {
                  window.location.reload();
                }}
                className="inline-flex items-center gap-2 bg-secondary-theme hover:bg-primary-theme-80 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
              >
                <svg 
                  className="w-5 h-5" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth="2" 
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Opnieuw proberen
              </button>
              
            </div>
          </div>
        </>
      )}

      {/* 3. Niet ingelogd & Geen demo & Nog geen wizard & NIET OFFLINE: Toon wizard */}
      {!isLoadingUser && !isOffline && !isAuthenticated && getCookie('demo') !== '1' && (
        <OnboardingWizard 
          onLoginRequest={handleLogin} 
          onCompleteNewOrg={handleCompleteWizard} 
        />
      )}

      {/* 4. Ingelogd, maar settings laden nog & NIET OFFLINE: Toon "Moment geduld..." spinner */}
      {!isLoadingUser && !isOffline && isAuthenticated && isLoading && !userSettings && (
        <InitialLoadingSpinner />
      )}
      
      {/* 5. Hoofd Dashboard UI - Alleen tonen als NIET offline */}
      {!isLoadingUser && !isOffline && (isAuthenticated || getCookie('demo') === '1') && !(isLoading && !userSettings) && (
        <>
        <SpotlightManager/>
          {/* Achtergrond op basis van thema instellingen */}
          {currentThemeColors.background.image ? (
            <div className="fixed inset-0 overflow-hidden" style={{ 
              backgroundImage: `url(${currentThemeColors.background.image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundAttachment: 'fixed',
              width: '100%',
              height: '100%',
              zIndex: -1,
            }}>
              <div className="absolute inset-0" style={{ 
                backgroundColor: `${currentThemeColors.background.color}`, 
                opacity: 0.25,
                backdropFilter: 'blur(1px)'
              }} />
            </div>
          ) : (
            <div className="fixed inset-0 z-[-1]" style={{ 
              backgroundColor: currentThemeColors.background.color 
            }}>
              {/* Wanneer geen achtergrondafbeelding is opgegeven, kun je AnimatedGradient tonen of gewoon de achtergrondkleur */}
              <AnimatedGradient />
            </div>
          )}

          {/* Content Container */}
          <div className="relative z-10 min-h-screen w-full overflow-auto text-theme">
            {/* Dynamic Favicon */}
            <DynamicFavicon 
              countdownTarget={countdownTarget}
              faviconUrl={getFaviconUrl()}
            />
            
            {/* Top Bar */}
            <div className={`fixed top-0 left-0 right-0 flex flex-col md:flex-row justify-between px-6 py-3 z-50 bg-gradient-to-b from-black/10 to-transparent transition-all duration-700 ease-in-out ${
              hideWidgets ? 'opacity-0 -translate-y-full' : 'opacity-100 translate-y-0'
            }`}>
              {/* Left Column - 1/3 */}
              <div className="w-full md:w-1/3 flex items-start justify-start">
                {/* Stats Display */} 
                {!hideWidgets && isAuthenticated && dashboardStats.length > 0 && (
                  <div className="flex items-center gap-4">
                    {dashboardStats.slice(0, 3).map((stat, index) => (
                      <StatDisplay key={index} stat={stat} />
                    ))}
                  </div>
                )}
                
                {/* Alleen login knop tonen als we zeker weten dat de gebruiker niet is ingelogd */}
                {!hideWidgets && !isLoadingUser && !isAuthenticated && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-theme hover:bg-primary-theme-10 rounded-full px-4 border-theme-20"
                    onClick={handleLogin}
                    loading={isLoginLoading}
                  >
                    Login
                  </Button>
                )}
              </div>
              {/* Center Column - 1/3 */}
              <div className="w-full md:w-1/3 flex items-start justify-center mb-4 md:mb-0">
                {!hideWidgets && (isAuthenticated || getCookie('demo') === '1') && (
                  <QuickLinksPill 
                    quickLinks={formatQuickLinks(userSettings?.quicklinks)}
                    onShowNotesOverlay={() => setShowNotesOverlay(true)}
                    onShowPomodoroSetup={() => setShowPomodoroSetup(true)}
                  />
                )}
              </div>
              
              {/* Right Column - 1/3 */}
              <div className="w-full md:w-1/3 flex items-start justify-end gap-4">
                {/* Notification Button */}
                {!hideWidgets && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative text-theme hover:bg-primary-theme-10 rounded-full"
                  onClick={() => setShowNotifications(true)}
                >
                  <Bell className="h-5 w-5" />
                  {unreadNotifications > 0 && (
                    <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 text-xs text-white flex items-center justify-center">
                      {unreadNotifications}
                    </span>
                  )}
                </Button>
                )}

                {/* Logo bovenaan de main content - Altijd zichtbaar */}
                {currentThemeColors.logo && ( 
                  <div className="flex items-center justify-end">
                    <img 
                      src={currentThemeColors.logo}
                      alt="Onespace Logo"
                      className="h-10 w-auto transition-all duration-100 ease-out" // Behoud vloeiende transitie
                    />
                  </div>
                )}
              </div>
            </div>
  
              {/* Poll Banner */} 
              {!hideWidgets && showPollBanner && currentPoll && !currentPoll.user_has_voted && (
                  <PollBanner 
                    pollId={currentPoll.id}
                    question={currentPoll.question}
                    imageUrl={currentPoll.image_url}
                    onOpenPoll={() => setIsPollModalOpen(true)}
                    onDismiss={() => setShowPollBanner(false)}
                  />
              )}
            {/* Main Content */}
            <main className="h-screen relative container mx-auto flex flex-col justify-center">
                    
                {/* Centrale klok wanneer widgets verborgen zijn */}
                {hideWidgets && (
                  <div className={`absolute inset-0 flex items-center justify-center transition-all duration-700 ease-in-out ${
                    hideWidgets ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-full'
                  }`}>
                    <ClockWidget 
                      id="central-clock"
                      title=""
                    />
                  </div>
                )}

                {/* Welkomstbericht & Snelle links */}
                <div className={`transition-all duration-700 ease-in-out ${
                  hideWidgets ? 'opacity-0 -translate-x-full' : 'opacity-100 '
                }`}>
                <CentralSection 
                  weatherData={weatherData || undefined} 
                  isWeatherLoading={isWeatherLoading} 
                  userName={userSettings?.username} 
                  settings={userSettings}
                  centralWidgets={userSettings?.central_widgets}
                />  
                </div>
              
            <div className={`absolute bottom-10 left-1/2 transform -translate-x-1/2 z-30 transition-all duration-700 ease-in-out ${
              hideWidgets ? 'opacity-0 translate-y-full' : 'opacity-100 translate-y-0'
            }`}>
              <div className="flex flex-col items-center px-4 py-3 min-w-[120px]">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="40"
                  viewBox="0 0 32 40"
                  fill="none"
                  className="mb-2 text-white opacity-90"
                >
                  <rect x="4" y="4" width="24" height=" 40" rx="12" stroke="white" strokeWidth="2.5" fill="none"/>
                  <circle cx="16" cy="14" r="2.5" fill="white"/>
                </svg>
                <span className="text-white text-sm font-light tracking-wide drop-shadow-md">
                  Scroll naar beneden
                </span>
              </div>
            </div>
            </main>
            <div className={`bg-primary-theme backdrop-blur-md transition-all duration-700 ease-in-out ${
              hideWidgets ? 'opacity-0 translate-x-full' : 'opacity-100 translate-x-0'
            }`}>
            <div className="container mx-auto px-6 pt-24 pb-20">
                  {/* Grid voor widgets - Nu na de parallax wrapper */}
              <div className="grid grid-cols-2 gap-6 mt-8">
                {/* Let op: isLoading wordt hier gebruikt ipv isReady */}
                {isLoading && !userSettings ? (
                  <div className="col-span-2 flex justify-center py-12">
                    {/* Gebruik hier eventueel ook de InitialLoadingSpinner ipv de kleine */}
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme"></div>
                  </div>
                ) : widgets.length === 0 ? (
                  <div className="col-span-2 text-center py-12 text-theme-80">
                    
                  </div>
                ) : (
                  widgets.map((widget, index) => (
                    <div
                      key={widget.id}
                      className="transition-transform duration-200"
                      style={{
                        gridColumnStart: widget.position.x + 1,
                        gridRowStart: widget.position.y + 1,
                        gridColumnEnd: `span ${widget.size.width}`,
                        gridRowEnd: `span ${widget.size.height}`,
                      }}
                    >
                      {renderWidget(widget, index)}
                    </div>
                  ))
                )}
              </div>
              </div>
            </div>

          
            {/* Settings Menu - placing in footer */}
            {!hideWidgets && isAuthenticated && (
              <SettingsMenu
                onOpenSettings={() => setIsSettingsOpen(true)}
                onOpenOrgSelector={() => setIsOrgSelectorOpen(true)}
                isAdmin={(user as any)?.is_admin}
              />
            )}
          </div>
        </>
            )}

      {/* OVERLAYS - Buiten getransformeerde containers om z-index problemen te voorkomen */}
            {/* Settings Dialog */}
            <SettingsDialog 
              isOpen={isSettingsOpen}
              onClose={() => setIsSettingsOpen(false)}
              onSave={handleSettingsSave}
              currentWidgets={userSettings?.widgets || []}
              onLogout={logout}
            />

            {/* Poll Modal */} 
            {currentPoll && (
              <PollModal 
                isOpen={isPollModalOpen}
                onClose={() => setIsPollModalOpen(false)}
                pollData={currentPoll}
                onSubmitVote={handleSubmitVote}
                isSubmitting={isSubmittingVote}
              />
            )}

            {/* Organization Selector Modal */}
            <OrganizationSelectorModal
              isOpen={isOrgSelectorOpen}
              onClose={() => setIsOrgSelectorOpen(false)}
            />

            {/* Quick Notes Overlay */}
            {showNotesOverlay && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
                <div className="bg-background rounded-lg shadow-xl max-w-md w-full p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Snelle Notitie</h3>
                    <button
                      onClick={() => setShowNotesOverlay(false)}
                      className="p-1.5 hover:bg-secondary rounded-lg transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <textarea
                    placeholder="Begin met typen..."
                    className="w-full h-32 p-3 bg-secondary/30 border border-border rounded-lg resize-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    autoFocus
                  />
                  <div className="mt-4 flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setShowNotesOverlay(false)}>
                      Annuleren
                    </Button>
                    <Button onClick={() => setShowNotesOverlay(false)}>
                      Opslaan
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Pomodoro Setup Overlay */}
            {showPomodoroSetup && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
                <div className="bg-background rounded-lg shadow-xl max-w-md w-full p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Pomodoro Timer</h3>
                    <button
                      onClick={() => setShowPomodoroSetup(false)}
                      className="p-1.5 hover:bg-secondary rounded-lg transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Taak</label>
                      <input
                        type="text"
                        placeholder="Waar ga je aan werken?"
                        value={pomodoroTask}
                        onChange={(e) => setPomodoroTask(e.target.value)}
                        className="w-full p-3 bg-secondary/30 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        autoFocus
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Duur (minuten)</label>
                      <select 
                        value={pomodoroDuration}
                        onChange={(e) => setPomodoroDuration(Number(e.target.value))}
                        className="w-full p-3 bg-secondary/30 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        <option value={15}>15 minuten</option>
                        <option value={25}>25 minuten</option>
                        <option value={30}>30 minuten</option>
                        <option value={45}>45 minuten</option>
                      </select>
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setShowPomodoroSetup(false)}>
                      Annuleren
                    </Button>
                    <Button onClick={handleStartPomodoro}>
                      Start Timer
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Notifications Modal */}
            {showNotifications && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
                <div className="bg-background rounded-lg shadow-xl max-w-md w-full p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Notificaties</h3>
                    <button
                      onClick={() => setShowNotifications(false)}
                      className="p-1.5 hover:bg-secondary rounded-lg transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Bell className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>Geen notificaties</p>
                      </div>
                    ) : (
                      notifications.map((notification, index) => (
                        <div
                          key={index}
                          className={`p-3 rounded-lg border transition-colors ${
                            notification.read_at ? 'bg-secondary/30' : 'bg-primary/10 border-primary/20'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-medium text-sm">{notification.title || 'Notificatie'}</h4>
                              <p className="text-sm text-muted-foreground mt-1">
                                {notification.message || notification.content}
                              </p>
                              {notification.created_at && (
                                <p className="text-xs text-muted-foreground mt-2">
                                  {new Date(notification.created_at).toLocaleString('nl-NL')}
                                </p>
                              )}
                            </div>
                            {!notification.read_at && (
                              <div className="w-2 h-2 bg-primary rounded-full ml-2 mt-1 flex-shrink-0" />
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="mt-4 flex justify-end">
                    <Button variant="outline" onClick={() => setShowNotifications(false)}>
                      Sluiten
                    </Button>
                  </div>
                </div>
              </div>
      )}
    </div>
  );
};

export default Dashboard; 